import { useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  DocumentThumbnail,
  DocumentViewer,
  MiButton,
  MiIcon,
  MiIconButton,
  MiModalConfirm,
  MiStackedTypography,
} from "@miplatform/components";
import { FONT_WEIGHTS, PERMISSIONS } from "@miplatform/constants";
import {
  ButtonVariants,
  IIssue,
  ISelectOption,
  ITrade,
} from "@miplatform/types";
import { getDefaultDate, getUpdatedFields } from "@miplatform/utils";
import {
  mdiCheck,
  mdiClose,
  mdiPencilOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { IssueFields } from "../InspectionDrawer/RedTagDrawer/IssueForm";
import { InspectionIssueStatus } from "./InspectionIssueStatus";

export const InspectionIssueCard = ({
  redTag,
  inspectionTrade,
  resolvedByTrade,
  causedByTrade,
  onEdit,
  onDelete,
  tradeOptions,
}: IInspectionIssueCardProps) => {
  const { classes, theme } = useStyles();
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  const { title, description, createdOn, documents } = redTag;
  const { hasPermission } = usePermissions();

  const {
    control,
    formState: { dirtyFields, isValid },
    getValues,
  } = useForm({
    defaultValues: {
      title,
      description,
      causedByTradeId: causedByTrade?.id || "",
      resolvedByTradeId: resolvedByTrade?.id || "",
    },
  });

  const canEdit = hasPermission(PERMISSIONS.TASK_ISSUE.UPDATE);
  const canDelete = hasPermission(PERMISSIONS.TASK_ISSUE.DELETE);

  const handleDocumentClick = () => {
    setShowImagesViewer(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(redTag.id, redTag.modifiedOn);
      setConfirmDeleteModalOpen(false);
    }
  };

  const handleSubmitEdit = (event: React.MouseEvent) => {
    event.preventDefault();
    const data: Partial<IIssue> = getUpdatedFields(dirtyFields, getValues());
    data.modifiedOn = redTag.modifiedOn;

    if (onEdit) {
      onEdit(redTag.id, data);
      setEditMode(false);
    }
  };

  return (
    <Box className={classes.redTagCard}>
      <Box className={classes.redTagCardBanner}>
        {/* TODO - remove Cathedral when we have dynamic trade vendors */}
        <Typography variant="HD1">
          Red Tag: {inspectionTrade?.name} - Cathedral Plumbing
        </Typography>
      </Box>
      <Box className={classes.redTagCardBody}>
        {createdOn && (
          <Box className={classes.issueFormContainer}>
            <InspectionIssueStatus
              status={redTag.status}
              date={getDefaultDate(createdOn)}
            />
          </Box>
        )}
        {editMode ? (
          <Box className={classes.redTagCardContent}>
            <IssueFields
              control={control}
              tradeOptions={tradeOptions}
              actions={
                <>
                  <MiButton
                    variant={ButtonVariants.Text}
                    color="success"
                    disabled={!isValid}
                    onClick={handleSubmitEdit}
                  >
                    <MiIcon path={mdiCheck} />
                  </MiButton>
                  <MiButton
                    variant={ButtonVariants.Text}
                    color="error"
                    onClick={() => setEditMode(false)}
                  >
                    <MiIcon path={mdiClose} />
                  </MiButton>
                </>
              }
            />
          </Box>
        ) : (
          <Box className={classes.redTagCardContent}>
            <Box className={classes.redTagCardTitleImageRow}>
              <Box display="flex" flexDirection="column">
                <Typography variant="HM1" fontWeight={FONT_WEIGHTS.SEMIBOLD}>
                  {title}
                </Typography>
                <Typography variant="BM1" color={theme.palette.text.secondary}>
                  {description}
                </Typography>
              </Box>
              <DocumentThumbnail
                documentCount={documents.length}
                document={documents[0]}
                onClick={handleDocumentClick}
              />
            </Box>
            <Box className={classes.redTagCardDetailsButtonsRow}>
              <Box display="flex" flex={1} justifyContent="space-between">
                <MiStackedTypography
                  title="Caused By"
                  description={causedByTrade?.name}
                />
                <MiStackedTypography
                  title="Resolved By"
                  description={resolvedByTrade?.name}
                />
                <MiStackedTypography title="Assigned To" description="--" />
              </Box>
              <Box display="flex" gap={1} justifyContent="flex-end">
                {onEdit && (
                  <MiIconButton
                    className={classes.editButton}
                    onClick={() => setEditMode(true)}
                    disabled={!canEdit}
                  >
                    <MiIcon path={mdiPencilOutline} size={0.75} />
                  </MiIconButton>
                )}
                {onDelete && (
                  <MiIconButton
                    className={classes.deleteButton}
                    onClick={() => setConfirmDeleteModalOpen(true)}
                    color="error"
                    disabled={!canDelete}
                  >
                    <MiIcon path={mdiTrashCanOutline} size={0.75} />
                  </MiIconButton>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <MiModalConfirm
        open={confirmDeleteModalOpen}
        handleNegative={() => setConfirmDeleteModalOpen(false)}
        handlePositive={handleConfirmDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this issue?"
      />
      <DocumentViewer
        documents={documents}
        onUnfullscreen={() => setShowImagesViewer(false)}
        fullScreenStatus={showImagesViewer}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  redTagCard: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    flexDirection: "column",
    overflow: "hidden",
  },
  redTagCardBanner: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(0, 1, 1),
  },
  redTagCardBody: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    minHeight: 60,
  },
  issueFormContainer: {
    width: 80,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  redTagCardContent: {
    flexDirection: "column",
    display: "flex",
    justifyContent: "space-between",
    flex: 1,
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    minWidth: 0,
  },
  redTagCardTitleImageRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  redTagCardDetailsButtonsRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(4),
    height: 34,
  },
  editButton: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
  },
  deleteButton: {
    backgroundColor: theme.palette.error.light,
    borderRadius: 4,
  },
}));

export interface IInspectionIssueCardProps {
  redTag: IIssue;
  inspectionTrade?: ITrade | null;
  resolvedByTrade?: ITrade | null;
  causedByTrade?: ITrade | null;
  onEdit?: (id: string, data: Partial<IIssue>) => void;
  onDelete?: (id: string, modifiedOn: string) => void;
  tradeOptions: ISelectOption[];
}
